import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Clock } from "lucide-react";

/**
 * Displays the last 20 pages created across all SEO categories.
 * Used on homepage, hubs, and all dynamic pages.
 */

interface RecentPage {
  href: string;
  label: string;
  type: string;
  date: string;
}

const typeEmoji: Record<string, string> = {
  film: "🎬",
  series: "📺",
  football: "⚽",
  ufc: "🥊",
};

const RecentPagesBlock = () => {
  const { data: recentPages } = useQuery({
    queryKey: ["recent-pages-block"],
    queryFn: async () => {
      const results: RecentPage[] = [];

      // Fetch latest from each category in parallel
      const [movies, series, matches, fights] = await Promise.all([
        supabase.from("movies").select("slug, title, created_at").eq("published", true).order("created_at", { ascending: false }).limit(5),
        supabase.from("series").select("slug, title, created_at").eq("published", true).order("created_at", { ascending: false }).limit(5),
        supabase.from("football_matches").select("slug, home_team, away_team, created_at").eq("published", true).order("created_at", { ascending: false }).limit(5),
        supabase.from("ufc_fights").select("slug, fighter_a, fighter_b, created_at").eq("published", true).order("created_at", { ascending: false }).limit(5),
      ]);

      (movies.data || []).forEach(m => results.push({ href: `/films/${m.slug}`, label: m.title, type: "film", date: m.created_at }));
      (series.data || []).forEach(s => results.push({ href: `/series/${s.slug}`, label: s.title, type: "series", date: s.created_at }));
      (matches.data || []).forEach(m => results.push({ href: `/football/${m.slug}`, label: `${m.home_team} vs ${m.away_team}`, type: "football", date: m.created_at }));
      (fights.data || []).forEach(f => results.push({ href: `/ufc/${f.slug}`, label: `${f.fighter_a} vs ${f.fighter_b}`, type: "ufc", date: f.created_at }));

      // Sort by date descending, take 20
      results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      return results.slice(0, 20);
    },
    staleTime: 10 * 60 * 1000,
  });

  if (!recentPages || recentPages.length === 0) return null;

  return (
    <section className="py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" />
          Ajoutés récemment
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {recentPages.map((p) => (
            <Link
              key={p.href}
              to={p.href}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-card border border-border/50 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors truncate"
            >
              <span>{typeEmoji[p.type] || "📄"}</span>
              <span className="truncate">{p.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RecentPagesBlock;
